import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Collapse,
  Alert,
  alpha,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Reddit as RedditIcon,
} from '@mui/icons-material';
import { analyzeApi } from '../../services/api';
import { trackSmartCompose } from '../../services/analytics';

const TONE_OPTIONS = [
  { value: 'engaging',      label: 'Engaging' },
  { value: 'professional',  label: 'Professional' },
  { value: 'casual',        label: 'Casual & Fun' },
  { value: 'educational',   label: 'Educational' },
  { value: 'inspirational', label: 'Inspirational' },
  { value: 'humorous',      label: 'Humorous' },
];

const PLATFORM_META = {
  instagram: { label: 'Instagram', color: '#e1306c', Icon: InstagramIcon },
  youtube:   { label: 'YouTube',   color: '#ff0000', Icon: YouTubeIcon },
  twitter:   { label: 'X (Twitter)', color: '#1da1f2', Icon: TwitterIcon },
  facebook:  { label: 'Facebook',  color: '#1877f2', Icon: FacebookIcon },
  linkedin:  { label: 'LinkedIn',  color: '#0a66c2', Icon: LinkedInIcon },
  reddit:    { label: 'Reddit',    color: '#ff4500', Icon: RedditIcon },
  tiktok:    { label: 'TikTok',    color: '#010101', Icon: AutoAwesomeIcon },
};

// ── Variation preview card ────────────────────────────────────────────────────

function VariationCard({ variation, index, selected, onSelect, theme }) {
  const isDark = theme.palette.mode === 'dark';
  const isSelected = selected === index;
  const hashtagCount = variation.hashtags?.length || 0;

  return (
    <Card
      elevation={0}
      sx={{
        border: `2px solid ${isSelected
          ? theme.palette.primary.main
          : alpha(theme.palette.divider, 0.6)}`,
        borderRadius: '10px',
        background: isSelected
          ? alpha(theme.palette.primary.main, isDark ? 0.15 : 0.06)
          : (isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02)),
        transition: 'all 0.15s ease',
        position: 'relative',
      }}
    >
      <CardActionArea disableRipple onClick={() => onSelect(index)} sx={{ p: 0 }}>
        <CardContent sx={{ p: 1.5, pb: '12px !important' }}>
          {/* Header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Chip
              label={`Option ${index + 1}`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.68rem',
                fontWeight: 700,
                background: isSelected
                  ? theme.palette.primary.main
                  : alpha(theme.palette.text.primary, 0.1),
                color: isSelected ? '#fff' : 'text.secondary',
              }}
            />
            {isSelected && (
              <CheckCircleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            )}
          </Box>

          {/* Title (YouTube / Reddit) */}
          {variation.title && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 700,
                fontSize: '0.78rem',
                color: 'text.primary',
                mb: 0.5,
                lineHeight: 1.4,
              }}
            >
              {variation.title}
            </Typography>
          )}

          {/* Body text preview */}
          <Typography
            variant="caption"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              color: 'text.secondary',
              fontSize: '0.78rem',
              lineHeight: 1.5,
            }}
          >
            {variation.text || '—'}
          </Typography>

          {/* Hashtag count badge */}
          {hashtagCount > 0 && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.75, color: 'primary.main', fontWeight: 600, fontSize: '0.7rem' }}
            >
              {hashtagCount} hashtags
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ── Picker dialog ─────────────────────────────────────────────────────────────

function SmartComposePicker({ open, suggestions, selected, onSelect, onApply, onClose, theme }) {
  const isDark = theme.palette.mode === 'dark';
  const platforms = Object.keys(suggestions || {});

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '14px',
          background: isDark ? '#1a1a2e' : '#fff',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <AutoAwesomeIcon sx={{ color: '#8b5cf6', fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            Choose Your Content
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select one variation per platform — then click Apply
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, px: 3 }}>
        {platforms.map((platform, idx) => {
          const meta = PLATFORM_META[platform] || { label: platform, color: '#6366f1', Icon: AutoAwesomeIcon };
          const { Icon, label, color } = meta;
          const variations = suggestions[platform] || [];

          return (
            <Box key={platform}>
              {/* Platform heading */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: idx > 0 ? 2.5 : 0 }}>
                <Box
                  sx={{
                    width: 30, height: 30, borderRadius: '8px',
                    background: alpha(color, 0.15),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Icon sx={{ fontSize: 18, color }} />
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {label}
                </Typography>
                <Chip
                  size="small"
                  label={`${variations.length} options`}
                  sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                />
              </Box>

              {/* Variation cards grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr 1fr',
                    sm: `repeat(${Math.min(variations.length, 3)}, 1fr)`,
                    md: `repeat(${Math.min(variations.length, 5)}, 1fr)`,
                  },
                  gap: 1.5,
                }}
              >
                {variations.map((variation, i) => (
                  <VariationCard
                    key={i}
                    variation={variation}
                    index={i}
                    selected={selected[platform] ?? 0}
                    onSelect={(idx) => onSelect(platform, idx)}
                    theme={theme}
                  />
                ))}
              </Box>

              {idx < platforms.length - 1 && (
                <Divider sx={{ mt: 2.5 }} />
              )}
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3, py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          gap: 1,
        }}
      >
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onApply}
          startIcon={<CheckCircleIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            px: 3,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
          }}
        >
          Apply Selected ({platforms.length} platform{platforms.length !== 1 ? 's' : ''})
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main SmartComposeBar ──────────────────────────────────────────────────────

/**
 * SmartComposeBar — "Write once, adapt all" AI composer.
 *
 * Generates 5 variations per platform and opens a picker dialog so the
 * user can choose one per platform before applying.
 *
 * Props:
 *   selectedPlatforms — string[] of currently selected platform keys
 *   onApplyResults    — (results: Record<string, {text, title?, hashtags?}>) => void
 */
export default function SmartComposeBar({ selectedPlatforms, onApplyResults }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [expanded,    setExpanded]    = useState(false);
  const [content,     setContent]     = useState('');
  const [tone,        setTone]        = useState('engaging');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [generated,   setGenerated]   = useState(false);

  // Picker state
  const [suggestions,  setSuggestions]  = useState(null); // {platform: [variation, ...]}
  const [pickerOpen,   setPickerOpen]   = useState(false);
  const [selected,     setSelected]     = useState({});   // {platform: index}

  const handleGenerate = useCallback(async () => {
    if (!content.trim()) {
      setError('Describe your topic or paste a draft to get started.');
      return;
    }
    if (selectedPlatforms.length === 0) {
      setError('Select at least one platform above first.');
      return;
    }

    setError(null);
    setLoading(true);
    setGenerated(false);

    try {
      const data = await analyzeApi.smartCompose(content.trim(), selectedPlatforms, tone);
      const platforms = data?.platforms;

      // Normalize: ensure every platform value is an array of variations
      const normalized = {};
      if (platforms && typeof platforms === 'object') {
        Object.entries(platforms).forEach(([platform, val]) => {
          normalized[platform] = Array.isArray(val) ? val : [val];
        });
      }

      const hasContent = Object.values(normalized).some(
        (vars) => vars.length > 0 && (vars[0]?.text || vars[0]?.title)
      );

      if (!hasContent) {
        setError('AI returned an unexpected response. Please try again.');
        return;
      }

      // Default selection: first variation per platform
      const defaults = {};
      Object.keys(normalized).forEach((p) => { defaults[p] = 0; });

      setSuggestions(normalized);
      setSelected(defaults);
      setPickerOpen(true);
    } catch (err) {
      const msg = err?.isQuotaError
        ? 'AI quota reached. Upgrade your plan or purchase top-up credits.'
        : (err?.message || 'Smart Compose failed. Please try again.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [content, tone, selectedPlatforms]);

  const handleSelectVariation = useCallback((platform, index) => {
    setSelected((prev) => ({ ...prev, [platform]: index }));
  }, []);

  const handleApply = useCallback(() => {
    if (!suggestions) return;
    const toApply = {};
    Object.entries(suggestions).forEach(([platform, variations]) => {
      toApply[platform] = variations[selected[platform] ?? 0];
    });
    onApplyResults(toApply);
    setPickerOpen(false);
    setGenerated(true);
    trackSmartCompose(Object.keys(toApply).length, tone);
  }, [suggestions, selected, onApplyResults, tone]);

  const handlePickerClose = useCallback(() => {
    setPickerOpen(false);
  }, []);

  return (
    <>
      <Box
        sx={{
          mb: 3,
          borderRadius: '8px',
          border: `1px solid ${alpha(isDark ? '#818cf8' : '#6366f1', 0.3)}`,
          background: isDark ? alpha('#6366f1', 0.06) : alpha('#6366f1', 0.03),
          overflow: 'hidden',
          transition: 'all 0.2s ease',
        }}
      >
        {/* Header — always visible */}
        <Box
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 2,
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { background: alpha('#6366f1', 0.05) },
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 22, color: '#8b5cf6' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
              Smart Compose
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Write once — AI generates multiple variations per platform for you to choose
            </Typography>
          </Box>
          {expanded ? (
            <ExpandLessIcon sx={{ color: 'text.secondary' }} />
          ) : (
            <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
          )}
        </Box>

        {/* Expandable body */}
        <Collapse in={expanded}>
          <Box sx={{ px: 3, pb: 3 }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              placeholder={'Describe your post idea, topic, or paste a rough draft...\n\nExample: "5 morning habits that doubled my productivity as a creator — sharing my routine with before/after results"'}
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                if (generated) setGenerated(false);
              }}
              disabled={loading}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.95rem' },
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Tone</InputLabel>
                <Select
                  value={tone}
                  label="Tone"
                  onChange={(e) => setTone(e.target.value)}
                  disabled={loading}
                  sx={{ borderRadius: '6px' }}
                >
                  {TONE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={loading || !content.trim()}
                startIcon={
                  loading
                    ? <CircularProgress size={18} color="inherit" />
                    : <AutoAwesomeIcon />
                }
                sx={{
                  borderRadius: '6px',
                  px: 3,
                  py: 1,
                  fontWeight: 800,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
                  '&.Mui-disabled': {
                    background: alpha('#6366f1', 0.3),
                    color: alpha('#fff', 0.5),
                  },
                }}
              >
                {loading
                  ? 'Generating variations...'
                  : generated
                    ? 'Regenerate'
                    : `Generate for ${selectedPlatforms.length} Platform${selectedPlatforms.length !== 1 ? 's' : ''}`}
              </Button>

              {generated && (
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                  Content applied to all platforms below ✓
                </Typography>
              )}

              {suggestions && !generated && !loading && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setPickerOpen(true)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '6px',
                    borderColor: alpha('#6366f1', 0.5),
                    color: '#6366f1',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#6366f1', background: alpha('#6366f1', 0.06) },
                  }}
                >
                  Review Selections
                </Button>
              )}
            </Box>

            {error && (
              <Alert
                severity={error.includes('quota') ? 'warning' : 'error'}
                onClose={() => setError(null)}
                sx={{ mt: 2, borderRadius: '6px' }}
              >
                {error}
              </Alert>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* Picker dialog — shown after generation */}
      {suggestions && (
        <SmartComposePicker
          open={pickerOpen}
          suggestions={suggestions}
          selected={selected}
          onSelect={handleSelectVariation}
          onApply={handleApply}
          onClose={handlePickerClose}
          theme={theme}
        />
      )}
    </>
  );
}
